import React, { forwardRef, useId } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '../theme';

// ============================================================================
// SWITCH
// ============================================================================

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  /**
   * Label for the switch.
   */
  label?: string;
  /**
   * Description text below the label.
   */
  description?: string;
  /**
   * Size of the switch.
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Toggle switch component for boolean values.
 * 
 * @example
 * // Basic switch
 * <Switch />
 * 
 * // With label
 * <Switch label="Enable notifications" />
 * 
 * // Controlled
 * <Switch 
 *   checked={isEnabled}
 *   onCheckedChange={setIsEnabled}
 *   label="Dark mode"
 * />
 */
const Switch = forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ 
  className, 
  label, 
  description,
  size = 'md',
  id: providedId,
  disabled,
  ...props 
}, ref) => {
  const generatedId = useId();
  const id = providedId ?? generatedId;

  const sizeClasses = {
    sm: {
      root: 'h-5 w-9',
      thumb: 'h-4 w-4 data-[state=checked]:translate-x-4',
    },
    md: {
      root: 'h-6 w-11',
      thumb: 'h-5 w-5 data-[state=checked]:translate-x-5',
    },
    lg: {
      root: 'h-7 w-14',
      thumb: 'h-6 w-6 data-[state=checked]:translate-x-7',
    },
  };

  const switchElement = (
    <SwitchPrimitive.Root
      ref={ref}
      id={id}
      disabled={disabled}
      className={cn(
        'peer inline-flex shrink-0 cursor-pointer items-center rounded-full',
        'border-2 border-transparent',
        'transition-colors duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:bg-sage data-[state=unchecked]:bg-warm-gray/30',
        sizeClasses[size].root,
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none block rounded-full bg-white shadow-lg',
          'ring-0 transition-transform duration-200',
          'data-[state=unchecked]:translate-x-0.5',
          sizeClasses[size].thumb
        )}
      />
    </SwitchPrimitive.Root>
  );

  if (!label && !description) {
    return switchElement;
  }

  return (
    <div className="flex items-center gap-3">
      {switchElement}
      <div className="grid gap-0.5 leading-none">
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'text-sm font-medium text-navy cursor-pointer',
              disabled && 'opacity-70 cursor-not-allowed'
            )}
          >
            {label}
          </label>
        )}
        {description && (
          <p className="text-sm text-warm-gray">
            {description}
          </p>
        )}
      </div>
    </div>
  );
});

Switch.displayName = SwitchPrimitive.Root.displayName;

// ============================================================================
// EXPORTS
// ============================================================================

export { Switch };
